import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  arrayUnion,
  limit,
} from "firebase/firestore";
import {
  X,
  Users,
  User,
  UserCheck,
  Mail,
  Phone,
  MoreVertical,
  Loader2,
  Check,
  Share2,
} from "lucide-react";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import featureGatingService from "../services/featureGatingService";
import SharingLimitDialog from "./SharingLimitDialog";
import { copyLabelsToUser } from "../services/labelService";
import { addTimelineEvent } from "../services/timelineService";
import toast from "react-hot-toast";

import SelectMenu from "./crm/SelectMenu";
/**
 * Bulk-share dialog for the All Cases screen.
 *
 * Mirrors the established per-case sharing model (AllowAccessDetails):
 * sharing == arrayUnion(recipientUids) into each case's `team`, plus
 * default-team sync for individuals, a sharing-limit check, label copy
 * and a timeline event.
 *
 * Props:
 *  - isOpen
 *  - onClose()
 *  - selectedCases: [{ id, collection }]
 *  - onShared()  — called after a successful share so the parent can refresh
 */
function ShareCasesModal({ isOpen, onClose, selectedCases = [], onShared }) {
  const { globalUser } = useAuth();
  const ownerUid = globalUser?.uid;

  // Recipients chosen so far: uid -> { uid, name, email, _isClient, viaIndividual }
  const [recipients, setRecipients] = useState({});

  // Teams
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [memberSelectionType, setMemberSelectionType] = useState("All Members");
  const [teamMembers, setTeamMembers] = useState([]); // [{uid, name, email}]
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);

  // Individual search
  const [searchType, setSearchType] = useState("Name"); // Name | Mobile Number | Email | Clients
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [debounced, setDebounced] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loadingSearch, setLoadingSearch] = useState(false);

  // Sharing limit dialog
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [limitDialogData, setLimitDialogData] = useState({
    currentCount: 0,
    maxLimit: 0,
    planName: "",
    upgradeMessage: "",
  });

  const [isSharing, setIsSharing] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Reset everything when the modal opens
  useEffect(() => {
    if (isOpen) {
      setRecipients({});
      setSelectedTeamId("");
      setMemberSelectionType("All Members");
      setTeamMembers([]);
      setSearchType("Name");
      setInputValue("");
      setDebounced("");
      setSearchResults([]);
    }
  }, [isOpen]);

  // Fetch all teams the user can share to:
  //  - teams they own (including the auto-created "Case/Cases shared to" default team)
  //  - Organisation teams they're a member of (owned by the admin, not the user)
  useEffect(() => {
    if (!isOpen || !ownerUid) return;
    const fetchTeams = async () => {
      setLoadingTeams(true);
      try {
        const [ownedSnap, memberSnap] = await Promise.all([
          getDocs(query(collection(db, "teams"), where("ownerId", "==", ownerUid))),
          getDocs(
            query(collection(db, "teams"), where("members", "array-contains", ownerUid))
          ),
        ]);

        const byId = {};
        ownedSnap.docs.forEach((d) => {
          byId[d.id] = { id: d.id, ...d.data() };
        });
        // Add org teams the user belongs to but doesn't own
        memberSnap.docs.forEach((d) => {
          const data = d.data();
          if (data.isOrgTeam && !byId[d.id]) byId[d.id] = { id: d.id, ...data };
        });

        setTeams(Object.values(byId));
      } catch (err) {
        console.error("Error fetching teams:", err);
      } finally {
        setLoadingTeams(false);
      }
    };
    fetchTeams();
  }, [isOpen, ownerUid]);

  // Fetch members when a team is selected
  const fetchTeamMembers = useCallback(async (teamId) => {
    if (!teamId) {
      setTeamMembers([]);
      return;
    }
    setLoadingTeamMembers(true);
    try {
      const teamSnap = await getDoc(doc(db, "teams", teamId));
      if (!teamSnap.exists()) {
        setTeamMembers([]);
        return;
      }
      const memberIds = teamSnap.data().members || [];
      const memberDocs = await Promise.all(
        memberIds.map(async (id) => {
          const l = await getDoc(doc(db, "lawyers", id));
          if (l.exists()) return { uid: l.id, ...l.data() };
          const c = await getDoc(doc(db, "client", id));
          if (c.exists()) return { uid: c.id, ...c.data(), _isClient: true };
          return null;
        })
      );
      setTeamMembers(memberDocs.filter(Boolean));
    } catch (err) {
      console.error("Error fetching team members:", err);
      setTeamMembers([]);
    } finally {
      setLoadingTeamMembers(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTeamId) fetchTeamMembers(selectedTeamId);
    else setTeamMembers([]);
  }, [selectedTeamId, fetchTeamMembers]);

  // Debounce the individual search input
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebounced(inputValue), 300);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [inputValue]);

  useEffect(() => {
    const term = debounced.trim();
    if (!term) {
      setSearchResults([]);
      return;
    }
    const run = async () => {
      const isClient = searchType === "Clients";
      const colRef = collection(db, isClient ? "client" : "lawyers");
      let field = "name";
      let value = term;
      if (searchType === "Mobile Number") field = "mobileNumber";
      else if (searchType === "Email") {
        field = "email";
        value = term.toLowerCase();
      }
      setLoadingSearch(true);
      try {
        const q = query(
          colRef,
          where(field, ">=", value),
          where(field, "<=", value + ""),
          limit(10)
        );
        const snap = await getDocs(q);
        setSearchResults(
          snap.docs.map((d) => ({ uid: d.id, ...d.data(), _isClient: isClient }))
        );
      } catch (err) {
        console.error("Search failed:", err);
        setSearchResults([]);
      } finally {
        setLoadingSearch(false);
      }
    };
    run();
  }, [debounced, searchType]);

  // Close type dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowTypeDropdown(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // ---- recipient helpers ----
  const addRecipient = (u, viaIndividual) => {
    if (!u?.uid || u.uid === ownerUid) return;
    setRecipients((prev) => ({
      ...prev,
      [u.uid]: {
        uid: u.uid,
        name: u.name || `User ${u.uid.substring(0, 5)}`,
        email: u.email || "",
        _isClient: Boolean(u._isClient),
        viaIndividual,
      },
    }));
  };
  const removeRecipient = (uid) => {
    setRecipients((prev) => {
      const next = { ...prev };
      delete next[uid];
      return next;
    });
  };
  const isRecipient = (uid) => Boolean(recipients[uid]);

  const handleTeamSelectionChange = (type) => {
    setMemberSelectionType(type);
    if (type === "All Members") {
      teamMembers.forEach((m) => addRecipient(m, false));
    }
  };

  // When members finish loading under "All Members", auto-add them
  useEffect(() => {
    if (selectedTeamId && memberSelectionType === "All Members") {
      teamMembers.forEach((m) => addRecipient(m, false));
    }
  }, [teamMembers]);

  const recipientList = Object.values(recipients);

  // ---- share ----
  const checkSharingLimit = async (uids) => {
    try {
      const [result, existing] = await Promise.all([
        featureGatingService.canAddTeamMember(ownerUid),
        featureGatingService.getUniqueTeamMemberIds(ownerUid),
      ]);
      const { maxLimit, planName, upgradeMessage } = result;
      const currentCount = existing.size;
      const newCount = uids.filter((u) => !existing.has(u)).length;
      if (currentCount + newCount > maxLimit) {
        setLimitDialogData({ currentCount, maxLimit, planName, upgradeMessage });
        setShowLimitDialog(true);
        return false;
      }
      return true;
    } catch (err) {
      console.error("Error checking sharing limit:", err);
      return false;
    }
  };

  const handleShare = async () => {
    const uids = recipientList.map((r) => r.uid);
    if (uids.length === 0) {
      toast.error("Select at least one person or team to share with.");
      return;
    }
    if (selectedCases.length === 0) {
      toast.error("No cases selected.");
      return;
    }

    const canProceed = await checkSharingLimit(uids);
    if (!canProceed) return;

    setIsSharing(true);
    try {
      const individualUids = recipientList
        .filter((r) => r.viaIndividual)
        .map((r) => r.uid);

      for (const c of selectedCases) {
        const caseRef = doc(db, c.collection, c.id);
        // 1) grant access
        await updateDoc(caseRef, { team: arrayUnion(...uids) });

        // 2) copy labels to recipients (best-effort, mirrors AllowAccess)
        try {
          const fresh = await getDoc(caseRef);
          const data = fresh.exists() ? fresh.data() : null;
          if (data?.labels && data.labels.length > 0) {
            const labelsByUser = data.labelsByUser || {};
            const ownerId = data.owner || ownerUid;
            if (!labelsByUser[ownerId]) labelsByUser[ownerId] = data.labels;
            for (const uid of uids) {
              try {
                const mapping = await copyLabelsToUser(ownerId, uid, data.labels);
                labelsByUser[uid] = data.labels
                  .map((id) => mapping[id])
                  .filter(Boolean);
              } catch (e) {
                console.error("Label copy failed for", uid, e);
              }
            }
            await updateDoc(caseRef, { labelsByUser });
          }
        } catch (e) {
          console.error("Label sync failed for case", c.id, e);
        }

        // 3) timeline (one concise event per case)
        try {
          addTimelineEvent(c.id, c.collection, {
            type: "case_shared",
            title: `Shared with ${uids.length} ${uids.length === 1 ? "person" : "people"}`,
            description: `Bulk shared from All Cases with: ${recipientList
              .map((r) => r.name)
              .join(", ")}`,
            performedBy: ownerUid || "",
            performedByName: globalUser?.name || globalUser?.displayName || "",
            changes: {},
          });
        } catch (e) {
          console.error("Timeline event failed for case", c.id, e);
        }
      }

      // 4) keep the owner's default "shared to" team in sync for individuals
      for (const uid of individualUids) {
        try {
          await featureGatingService.addMemberToDefaultTeam(ownerUid, uid);
        } catch (e) {
          console.error("Default team sync failed for", uid, e);
        }
      }

      toast.success(
        `Shared ${selectedCases.length} ${selectedCases.length === 1 ? "case" : "cases"} with ${uids.length} ${uids.length === 1 ? "person" : "people"}.`
      );
      if (onShared) await onShared();
      onClose();
    } catch (err) {
      console.error("Error sharing cases:", err);
      toast.error("Failed to share cases. Please try again.");
    } finally {
      setIsSharing(false);
    }
  };

  if (!isOpen) return null;

  const searchTypeOptions = [
    { value: "Name", icon: <User size={16} className="text-gray-400" /> },
    { value: "Mobile Number", icon: <Phone size={16} className="text-gray-400" /> },
    { value: "Email", icon: <Mail size={16} className="text-gray-400" /> },
    { value: "Clients", icon: <UserCheck size={16} className="text-gray-400" /> },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A061E] rounded-2xl border border-gray-700 w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-yellow-500 flex items-center gap-2">
            <Share2 size={18} />
            Share {selectedCases.length}{" "}
            {selectedCases.length === 1 ? "case" : "cases"}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-6">
          {/* Selected recipients */}
          {recipientList.length > 0 && (
            <div>
              <label className="text-gray-300 text-sm font-medium mb-2 block">
                Sharing with ({recipientList.length})
              </label>
              <div className="flex flex-wrap gap-2">
                {recipientList.map((r) => (
                  <span
                    key={r.uid}
                    className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 bg-[#1e1b4b] text-white rounded-full text-xs border border-gray-700"
                  >
                    {r.name}
                    {r._isClient && (
                      <span className="text-purple-300">(Client)</span>
                    )}
                    <button
                      onClick={() => removeRecipient(r.uid)}
                      className="p-0.5 hover:bg-gray-700 rounded-full"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Share to a team */}
          <div className="space-y-3">
            <h3 className="text-[#D19B15] font-medium flex items-center gap-2">
              <Users size={16} /> Share to a team
            </h3>
            <SelectMenu
              value={selectedTeamId}
              onChange={(e) => {
                setSelectedTeamId(e.target.value);
                setMemberSelectionType("All Members");
              }}
              className="w-full bg-[#1e1b4b] text-white p-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#D19B15]"
              disabled={loadingTeams}
            >
              <option value="">
                {loadingTeams
                  ? "Loading teams..."
                  : teams.length > 0
                  ? "Choose a team"
                  : "No teams found"}
              </option>
              {teams
                .filter((t) => !t.isDefault)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.teamName}
                    {t.isOrgTeam ? " — Organisation" : ""}
                  </option>
                ))}
            </SelectMenu>

            {selectedTeamId && (
              <SelectMenu
                value={memberSelectionType}
                onChange={(e) => handleTeamSelectionChange(e.target.value)}
                className="w-full bg-[#1e1b4b] text-white p-3 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#D19B15]"
                disabled={loadingTeamMembers}
              >
                <option value="All Members">All Members</option>
                <option value="Specific Members">Specific Members</option>
              </SelectMenu>
            )}

            {selectedTeamId && memberSelectionType === "Specific Members" && (
              <div className="bg-[#1e1b4b] rounded-lg border border-gray-700 max-h-48 overflow-y-auto divide-y divide-gray-700">
                {loadingTeamMembers ? (
                  <div className="p-4 text-center text-gray-400">
                    <Loader2 className="animate-spin inline mr-2" size={16} />
                    Loading members...
                  </div>
                ) : teamMembers.length === 0 ? (
                  <p className="p-4 text-center text-gray-400 text-sm">
                    No members in this team.
                  </p>
                ) : (
                  teamMembers.map((m) => (
                    <label
                      key={m.uid}
                      className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#2d2a5a]"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-yellow-500"
                        checked={isRecipient(m.uid)}
                        onChange={(e) =>
                          e.target.checked
                            ? addRecipient(m, false)
                            : removeRecipient(m.uid)
                        }
                      />
                      <span className="text-white text-sm">
                        {m.name || `User ${m.uid.substring(0, 5)}`}
                        {m.email && (
                          <span className="text-gray-400 ml-2 text-xs">
                            {m.email}
                          </span>
                        )}
                      </span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Share to individuals */}
          <div className="space-y-3">
            <h3 className="text-[#D19B15] font-medium flex items-center gap-2">
              <User size={16} /> Share to individuals
            </h3>
            <div className="relative">
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={`Search by ${searchType}`}
                  className="flex-1 px-4 py-2 rounded-lg bg-[#1e1b4b] text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#D19B15]"
                />
                <button
                  type="button"
                  onClick={() => setShowTypeDropdown((v) => !v)}
                  className="p-2 rounded-lg bg-[#1e1b4b] text-gray-400 hover:text-white border border-gray-700"
                >
                  <MoreVertical size={20} />
                </button>
                {loadingSearch && (
                  <Loader2
                    className="absolute right-12 top-1/2 -translate-y-1/2 animate-spin text-gray-500"
                    size={18}
                  />
                )}
              </div>
              {showTypeDropdown && (
                <div
                  ref={dropdownRef}
                  className="absolute right-0 top-full mt-2 w-48 bg-[#1e1b4b] rounded-lg shadow-lg z-10 overflow-hidden border border-gray-700"
                >
                  {searchTypeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setSearchType(opt.value);
                        setShowTypeDropdown(false);
                        setInputValue("");
                        setSearchResults([]);
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-700 text-gray-300 flex items-center gap-3 text-sm"
                    >
                      {opt.icon}
                      {opt.value}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {searchResults.length > 0 && (
              <div className="bg-[#1e1b4b] rounded-lg border border-gray-700 max-h-56 overflow-y-auto divide-y divide-gray-700">
                {searchResults.map((r) => (
                  <label
                    key={r.uid}
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-[#2d2a5a]"
                  >
                    <span className="flex flex-col">
                      <span className="text-white text-sm font-medium">
                        {r.name}
                        {r._isClient && (
                          <span className="ml-2 text-[10px] uppercase bg-purple-500/20 text-purple-300 rounded-full px-2 py-0.5">
                            Client
                          </span>
                        )}
                      </span>
                      {r.email && (
                        <span className="text-gray-400 text-xs">{r.email}</span>
                      )}
                    </span>
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-yellow-500"
                      checked={isRecipient(r.uid)}
                      onChange={(e) =>
                        e.target.checked
                          ? addRecipient(r, true)
                          : removeRecipient(r.uid)
                      }
                    />
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg text-sm hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleShare}
            disabled={isSharing || recipientList.length === 0}
            className="inline-flex items-center gap-2 px-5 py-2 bg-yellow-500 hover:bg-yellow-600 text-gray-900 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSharing ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Check size={16} />
            )}
            {isSharing
              ? "Sharing..."
              : `Share with ${recipientList.length || ""}`.trim()}
          </button>
        </div>
      </div>

      <SharingLimitDialog
        isOpen={showLimitDialog}
        onClose={() => setShowLimitDialog(false)}
        currentCount={limitDialogData.currentCount}
        maxLimit={limitDialogData.maxLimit}
        planName={limitDialogData.planName}
        upgradeMessage={limitDialogData.upgradeMessage}
      />
    </div>
  );
}

export default ShareCasesModal;
